import { CHAT_LIST_PAGE_SIZE, CHAT_MESSAGES_PAGE_MAX, CHAT_MESSAGES_PAGE_SIZE } from '../config/constants';
import { chatRequest } from './client';
import type { ChatRuntime } from './runtime';
import type {
  ChatAvailableUsersResultDTO,
  ChatUsersSearchResultDTO,
  ChatDetailDTO,
  ChatListResultDTO,
  ChatMessagesResultDTO,
  CreateReportResultDTO,
  ChatMessageDTO,
  ChatUnreadCountResultDTO,
  CreatePersonalChatResultDTO,
  CreateGroupChatResultDTO,
  UpdateGroupChatResultDTO,
  LeaveChatResultDTO,
  DeleteChatResultDTO,
  ToggleGroupAdminResultDTO,
  ToggleChatArchiveResultDTO,
  PinMessageResultDTO,
  MarkChatReadResultDTO,
  ChatReportTargetType,
} from './dto';

function postReport(
  runtime: ChatRuntime,
  input: {
    targetType: ChatReportTargetType;
    targetId: number;
    reason: string;
    comment?: string;
    attachments?: File[];
  },
) {
  const trimmedComment = input.comment?.trim();

  if (input.attachments?.length) {
    const formData = new FormData();
    formData.append('targetType', input.targetType);
    formData.append('targetId', String(input.targetId));
    formData.append('reason', input.reason);
    if (trimmedComment) {
      formData.append('comment', trimmedComment);
    }
    for (const file of input.attachments) {
      formData.append('attachments', file);
    }

    return chatRequest<CreateReportResultDTO>(runtime, '/reports', {
      method: 'POST',
      body: formData,
    });
  }

  const body: {
    targetType: ChatReportTargetType;
    targetId: number;
    reason: string;
    comment?: string;
  } = {
    targetType: input.targetType,
    targetId: input.targetId,
    reason: input.reason,
  };

  if (trimmedComment) {
    body.comment = trimmedComment;
  }

  return chatRequest<CreateReportResultDTO>(runtime, '/reports', {
    method: 'POST',
    body: JSON.stringify(body),
  });
}

export function createChatApi(runtime: ChatRuntime) {
  const api = {
    listChats(
      params: {
        type?: 'personal' | 'group';
        origin?: 'system' | 'user';
        isArchived?: boolean;
        status?: 'active' | 'closed';
        search?: string;
        page?: number;
        pageSize?: number;
        sort?: 'lastMessageAt:desc' | 'lastMessageAt:asc' | 'title:asc' | 'title:desc';
      } = {},
    ) {
      const sp = new URLSearchParams();

      if (params.type) sp.set('type', params.type);
      if (params.origin) sp.set('origin', params.origin);
      if (typeof params.isArchived === 'boolean') sp.set('isArchived', String(params.isArchived));
      if (params.status) sp.set('status', params.status);
      if (params.search?.trim()) sp.set('search', params.search.trim());

      sp.set('page', String(params.page ?? 1));
      sp.set('pageSize', String(params.pageSize ?? CHAT_LIST_PAGE_SIZE));
      sp.set('sort', params.sort ?? 'lastMessageAt:desc');

      return chatRequest<ChatListResultDTO>(runtime, `/chats?${sp.toString()}`);
    },

    listArchivedChats(
      params: {
        type?: 'personal' | 'group';
        origin?: 'system' | 'user';
        status?: 'active' | 'closed';
        search?: string;
        page?: number;
        pageSize?: number;
        sort?: 'lastMessageAt:desc' | 'lastMessageAt:asc' | 'title:asc' | 'title:desc';
      } = {},
    ) {
      return api.listChats({ ...params, isArchived: true });
    },

    listMessages(chatId: number, params: { beforeMessageId?: number; limit?: number } = {}) {
      const sp = new URLSearchParams();

      if (params.beforeMessageId) {
        sp.set('beforeMessageId', String(params.beforeMessageId));
      }

      const limit = Math.min(params.limit ?? CHAT_MESSAGES_PAGE_SIZE, CHAT_MESSAGES_PAGE_MAX);
      sp.set('limit', String(limit));

      return chatRequest<ChatMessagesResultDTO>(
        runtime,
        `/chats/${chatId}/messages?${sp.toString()}`,
      );
    },

    getChatDetail(chatId: number) {
      return chatRequest<ChatDetailDTO>(runtime, `/chats/${chatId}`);
    },

    getUnreadCount() {
      return chatRequest<ChatUnreadCountResultDTO>(runtime, '/chats/unread-count');
    },

    createMessage(input: {
      chatId: number;
      text?: string;
      replyToMessageId?: number;
      clientMessageId?: string;
      attachments?: File[];
    }) {
      const { chatId, text, replyToMessageId, clientMessageId, attachments } = input;
      const trimmedText = text?.trim() ?? '';

      if (!trimmedText && !attachments?.length) {
        return Promise.reject(new Error('Message must include text or attachments'));
      }

      if (attachments?.length) {
        const formData = new FormData();

        if (trimmedText) formData.append('text', trimmedText);
        if (replyToMessageId) formData.append('replyToMessageId', String(replyToMessageId));
        if (clientMessageId) formData.append('clientMessageId', clientMessageId);

        attachments.forEach((file) => formData.append('attachments', file));

        return chatRequest<ChatMessageDTO>(runtime, `/chats/${chatId}/messages`, {
          method: 'POST',
          body: formData,
        });
      }

      return chatRequest<ChatMessageDTO>(runtime, `/chats/${chatId}/messages`, {
        method: 'POST',
        body: JSON.stringify({
          text: trimmedText,
          replyToMessageId,
          clientMessageId,
        }),
      });
    },

    markRead(chatId: number, messageIds: number[]) {
      const uniqueIds = [...new Set(messageIds)].filter((id) => Number.isInteger(id) && id > 0);

      return chatRequest<MarkChatReadResultDTO>(runtime, `/chats/${chatId}/read`, {
        method: 'POST',
        body: JSON.stringify({ messageIds: uniqueIds }),
      });
    },

    listAvailableUsers(
      params: { search?: string; accountType?: 'student' | 'staff' } = {},
    ) {
      const sp = new URLSearchParams();

      const search = params.search?.trim().slice(0, 255);
      if (search) {
        sp.set('search', search);
      }

      if (params.accountType) {
        sp.set('accountType', params.accountType);
      }

      const qs = sp.toString();

      return chatRequest<ChatAvailableUsersResultDTO>(
        runtime,
        qs ? `/chats/available-users?${qs}` : '/chats/available-users',
      );
    },

    searchUsers(search: string) {
      const trimmed = search.trim();

      if (!trimmed) {
        return Promise.resolve({ users: [] } satisfies ChatUsersSearchResultDTO);
      }

      const sp = new URLSearchParams();
      sp.set('search', trimmed);

      return chatRequest<ChatUsersSearchResultDTO>(runtime, `/chats/users?${sp.toString()}`);
    },

    createPersonalChat(userId: number) {
      return chatRequest<CreatePersonalChatResultDTO>(runtime, '/chats/personal', {
        method: 'POST',
        body: JSON.stringify({ userId }),
      });
    },

    createGroupChat(input: { title: string; avatarFile: File; participantIds?: number[] }) {
      const formData = new FormData();
      formData.append('title', input.title.trim());
      formData.append('avatarFile', input.avatarFile);

      if (input.participantIds?.length) {
        formData.append('participantIds', JSON.stringify(input.participantIds));
      }

      return chatRequest<CreateGroupChatResultDTO>(runtime, '/chats/group', {
        method: 'POST',
        body: formData,
      });
    },

    leaveChat(chatId: number) {
      return chatRequest<LeaveChatResultDTO>(runtime, `/chats/${chatId}/leave`, {
        method: 'POST',
      });
    },

    deleteChat(chatId: number) {
      return chatRequest<DeleteChatResultDTO>(runtime, `/chats/${chatId}`, {
        method: 'DELETE',
      });
    },

    toggleChatArchive(chatId: number) {
      return chatRequest<ToggleChatArchiveResultDTO>(runtime, `/chats/${chatId}/archive`, {
        method: 'POST',
      });
    },

    pinMessage(chatId: number, messageId: number) {
      return chatRequest<PinMessageResultDTO>(runtime, `/chats/${chatId}/pin`, {
        method: 'PUT',
        body: JSON.stringify({ messageId }),
      });
    },

    unpinMessage(chatId: number) {
      return chatRequest<null>(runtime, `/chats/${chatId}/pin`, {
        method: 'DELETE',
      });
    },

    toggleGroupAdmin(chatId: number, userIds: number[]) {
      return chatRequest<ToggleGroupAdminResultDTO>(runtime, `/chats/${chatId}/admin`, {
        method: 'POST',
        body: JSON.stringify({ userIds }),
      });
    },

    updateGroupChat(
      chatId: number,
      input: {
        title?: string;
        status?: 'active' | 'closed';
        avatarFile?: File;
        addParticipantIds?: number[];
        removeParticipantIds?: number[];
      },
    ) {
      const formData = new FormData();

      if (input.title?.trim()) {
        formData.append('title', input.title.trim());
      }

      if (input.status) {
        formData.append('status', input.status);
      }

      if (input.avatarFile) {
        formData.append('avatarFile', input.avatarFile);
      }

      if (input.addParticipantIds?.length) {
        formData.append('addParticipantIds', JSON.stringify(input.addParticipantIds));
      }

      if (input.removeParticipantIds?.length) {
        formData.append('removeParticipantIds', JSON.stringify(input.removeParticipantIds));
      }

      return chatRequest<UpdateGroupChatResultDTO>(runtime, `/chats/${chatId}/group`, {
        method: 'PUT',
        body: formData,
      });
    },

    createReport(input: {
      messageId: number;
      reason: string;
      comment?: string;
      attachments?: File[];
    }) {
      return postReport(runtime, {
        targetType: 'chat_message',
        targetId: input.messageId,
        reason: input.reason,
        comment: input.comment,
        attachments: input.attachments,
      });
    },

    createChatReport(input: {
      chatId: number;
      reason: string;
      comment?: string;
      attachments?: File[];
    }) {
      return postReport(runtime, {
        targetType: 'chat',
        targetId: input.chatId,
        reason: input.reason,
        comment: input.comment,
        attachments: input.attachments,
      });
    },
  };

  return api;
}

export type ChatApi = ReturnType<typeof createChatApi>;
